import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { CommentTemplatesSheet } from '@/components/observation/CommentTemplatesSheet';
import { Icon } from '@/components/ui/Icon';
import { LogoImage } from '@/components/ui/LogoImage';
import { StarRating } from '@/components/ui/StarRating';
import { Colors } from '@/constants/Colors';
import {
  filterCommentTemplatesForPoste,
  loadAdvancedCommentTemplatesPreference,
  saveAdvancedCommentTemplatesPreference,
  type CommentTemplate,
} from '@/constants/observationCommentTemplates';
import { useTheme } from '@/hooks/useTheme';
import { apiService } from '@/services/api';
import { trackUsageEvent } from '@/services/usageEvent';
import {
  canEditObservation,
  resolveObservationTeamSide,
  type TeamSideContext,
} from '@/services/observationTeam';
import { aggregateObservationScores, computeObservationTrust } from '@/services/observationScoring';
import { useClubStore } from '@/stores/clubStore';
import { useProfileStore } from '@/stores/profileStore';
import { formatObservationPlayerLabel, isObservationVisibleToViewer } from '@/utils/observationDisplay';
import { resolveObservationPrivate, setLocalObservationPrivate } from '@/utils/observationPrivacy';
import { getMatchObservationBlockMessage } from '@/utils/matchKickoff';
import {
  buildAppUrl,
  buildMatchShareUrl,
  buildObservationShareUrl,
  shareContent,
  shareResultToFeedback,
} from '@/utils/shareLink';
import type { Match, Observation } from '@/types';

const MAX_RATING = 10;
const MAX_STARS = 10;
const GEO_DISTANCE_THRESHOLD_M = 500;
/** Valeurs affichées à droite de chaque critère (note /10) — aligné sur les listes d’observations */
const RATING_NOTE_COLOR = '#fdd663';

type TeamSide = 'home' | 'away';

interface ObservationFormState {
  nom: string;
  prenom: string;
  poste: string;
  numero: string;
  selectedTeam: TeamSide;
  /** Note globale 0–10 (étoiles) ; alignée sur les critères /10. */
  noteGlobale: number;
  technique: number;
  physique: number;
  mental: number;
  tactique: number;
  vitesse: number;
  nbButs: number;
  nbPassesDecisives: number;
  isPrivate: boolean;
  comments: string;
}

interface ObservationRouteState {
  mode?: 'create' | 'edit';
  /** Liste « Mes observations » (Scouting) : édition autorisée si connecté. */
  trustedMineList?: boolean;
  observation?: Observation;
  matchId?: string;
  matchRouteId?: string;
  matchLatitude?: string;
  matchLongitude?: string;
  homeTeam?: string;
  awayTeam?: string;
  homeLogo?: string;
  awayLogo?: string;
  homeClubId?: string;
  awayClubId?: string;
  homeClubCle?: string;
  awayClubCle?: string;
  matchCompetition?: string;
  matchPoule?: string;
}

const posteOptions = ['GARDIEN', 'DEFENSEUR', 'MILIEU', 'ATTAQUANT'] as const;

const defaultFormState: ObservationFormState = {
  nom: '',
  prenom: '',
  poste: '',
  numero: '',
  selectedTeam: 'home',
  noteGlobale: 6,
  technique: 6,
  physique: 6,
  mental: 6,
  tactique: 6,
  vitesse: 6,
  nbButs: 0,
  nbPassesDecisives: 0,
  isPrivate: false,
  comments: '',
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function starsToCriterion(stars: number): number {
  return clamp(Math.round(stars), 0, MAX_STARS);
}

function criteriaToStars(
  technique: number,
  physique: number,
  mental: number,
  tactique: number,
  vitesse: number,
): number {
  const values = [technique, physique, mental, tactique, vitesse].filter((v) => Number.isFinite(v));
  if (!values.length) return 0;
  const avg = values.reduce((acc, v) => acc + v, 0) / values.length;
  return clamp(Math.round(avg), 0, MAX_STARS);
}

function criteriaSpread(
  technique: number,
  physique: number,
  mental: number,
  tactique: number,
  vitesse: number,
): number {
  const values = [technique, physique, mental, tactique, vitesse];
  return Math.max(...values) - Math.min(...values);
}

function findClubByName<T extends { nom: string; cle?: string; idclub: string }>(
  clubs: T[],
  teamName: string | undefined,
): T | undefined {
  const q = (teamName || '').trim().toLowerCase();
  if (!q) return undefined;
  return clubs.find((c) => {
    const name = (c.nom || '').trim().toLowerCase();
    return name === q || name.includes(q) || q.includes(name);
  });
}

function inferPosteFromNumero(rawNumero: string): string | null {
  const numero = Number.parseInt(rawNumero.replace(/\D/g, ''), 10);
  if (!Number.isFinite(numero)) return null;
  if (numero === 1) return 'GARDIEN';
  if (numero >= 2 && numero <= 5) return 'DEFENSEUR';
  if (numero === 6 || numero === 8 || numero === 10) return 'MILIEU';
  if (numero === 7 || numero === 9 || numero === 11) return 'ATTAQUANT';
  return null;
}

type GeoVerificationState = 'not_requested' | 'not_available' | 'pending' | 'verified_nearby' | 'verified_far' | 'uncertain';

function haversineDistanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const earthRadius = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadius * c;
}

function getContentQuality(form: ObservationFormState): {
  score: number;
  level: 'Faible' | 'Moyenne' | 'Élevée';
  wordCount: number;
  hasConcreteElements: boolean;
  hasStructuredNotes: boolean;
} {
  const comment = form.comments.trim();
  const wordCount = comment ? comment.split(/\s+/).filter(Boolean).length : 0;
  const hasConcreteElements = /\b(\d+e?|\d+\/10|minute|pressing|duel|appel|transition|passe|tir|replacement|positionnement)\b/i.test(comment);
  const hasStructuredNotes = [form.technique, form.physique, form.vitesse, form.mental, form.tactique].every((v) => Number.isFinite(v) && v > 0);
  const ratingSpread = Math.max(form.technique, form.physique, form.vitesse, form.mental, form.tactique) - Math.min(form.technique, form.physique, form.vitesse, form.mental, form.tactique);

  let score = 0;
  if (wordCount >= 10) score += 25;
  if (wordCount >= 20) score += 15;
  if (hasConcreteElements) score += 20;
  if (hasStructuredNotes) score += 20;
  if (ratingSpread >= 2) score += 10; // Encourages discriminating evaluation instead of flat scores.
  if (form.nom.trim() && form.prenom.trim() && form.numero.trim() && form.poste.trim()) score += 10;

  const normalized = Math.max(0, Math.min(100, score));
  const level: 'Faible' | 'Moyenne' | 'Élevée' = normalized >= 75 ? 'Élevée' : normalized >= 45 ? 'Moyenne' : 'Faible';
  return { score: normalized, level, wordCount, hasConcreteElements, hasStructuredNotes };
}

function mapQualityLevel(level: 'Faible' | 'Moyenne' | 'Élevée'): 'FAIBLE' | 'MOYENNE' | 'ELEVEE' {
  if (level === 'Faible') return 'FAIBLE';
  if (level === 'Moyenne') return 'MOYENNE';
  return 'ELEVEE';
}

function mapGeoStatus(state: GeoVerificationState): 'NON_VERIFIE' | 'PROCHE_MATCH' | 'HORS_ZONE' | 'INCERTAIN' | 'INDISPONIBLE' {
  if (state === 'verified_nearby') return 'PROCHE_MATCH';
  if (state === 'verified_far') return 'HORS_ZONE';
  if (state === 'uncertain') return 'INCERTAIN';
  if (state === 'not_available') return 'INDISPONIBLE';
  return 'NON_VERIFIE';
}

function boolToFlag(value: boolean): 0 | 1 {
  return value ? 1 : 0;
}

function getSpeechRecognitionConstructor(): (new () => SpeechRecognition) | null {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

function observationMatchesShareId(obs: Observation, shareId: string): boolean {
  const q = shareId.trim();
  if (!q) return false;
  return obs.id === q || (obs.cle != null && obs.cle === q);
}

function formatFicheObservationMeta(obs: Observation, viewerNameFallback?: string): string | null {
  const dateRaw = obs.date?.trim();
  let datePart = '';
  if (dateRaw) {
    const d = new Date(dateRaw);
    if (!Number.isNaN(d.getTime())) {
      datePart = `Le ${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}`;
    }
  }
  const author = (obs.author?.trim() || viewerNameFallback?.trim() || '').trim();
  if (datePart && author) return `${datePart} par ${author}`;
  if (datePart) return datePart;
  if (author) return `Par ${author}`;
  return null;
}

function CriteriaSlider({ label, value, onChange, min = 1, max = MAX_RATING, showMax = true, colors, disabled = false }: {
  label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; showMax?: boolean; colors: any; disabled?: boolean;
}) {
  const range = max - min + 1;
  const percentage = ((value - min) / (range - 1)) * 100;
  return (
    <div style={{ borderRadius: 16, padding: 16, marginBottom: 12, backgroundColor: colors.surface }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: 14, fontWeight: 500, color: colors.text }}>{label}</span>
        <span style={{ fontSize: 16, fontWeight: 700, color: RATING_NOTE_COLOR }}>{showMax ? `${value}/${max}` : `${value}`}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          onClick={() => onChange(clamp(value - 1, min, max))}
          disabled={disabled}
          style={{ ...styles.stepperButton, color: colors.textSecondary, borderColor: colors.border, backgroundColor: colors.background }}
          aria-label={`Diminuer ${label}`}
        >
          -
        </button>
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          disabled={disabled}
          style={{ ...styles.rangeInput, accentColor: RATING_NOTE_COLOR }}
          aria-label={`Curseur ${label}`}
        />
        <button
          onClick={() => onChange(clamp(value + 1, min, max))}
          disabled={disabled}
          style={{ ...styles.stepperButton, color: colors.textSecondary, borderColor: colors.border, backgroundColor: colors.background }}
          aria-label={`Augmenter ${label}`}
        >
          +
        </button>
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: colors.textSecondary, textAlign: 'right' }}>
        {Math.round(percentage)}%
      </div>
    </div>
  );
}

export default function DetailedObservationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigationType = useNavigationType();
  const colors = useTheme();
  const fieldStyle = useMemo(
    () =>
      ({
        width: '100%',
        height: 48,
        borderRadius: 12,
        border: `1px solid ${Colors.primary}25`,
        backgroundColor: colors.surface,
        padding: '0 12px',
        outline: 'none',
        color: colors.text,
        fontSize: 14,
      }) as React.CSSProperties,
    [colors.surface, colors.text],
  );
  const selectOptionStyle = useMemo(
    () =>
      ({
        color: colors.text,
        backgroundColor: colors.surface,
      }) as React.CSSProperties,
    [colors.text, colors.surface],
  );
  const routeState = (location.state as ObservationRouteState | null) ?? {};
  const sourceObservation = routeState.observation;
  const shareHydratedRef = useRef<string | null>(null);
  const isEditMode = routeState.mode === 'edit' && Boolean(sourceObservation);
  const tifosiCle = useProfileStore((s) => s.tifosiCle);
  const googleId = useProfileStore((s) => s.googleId);
  const profileEmail = useProfileStore((s) => s.email);
  const profilePrenom = useProfileStore((s) => s.prenom);
  const profileNom = useProfileStore((s) => s.nom);
  const profilePseudo = useProfileStore((s) => s.pseudo);

  const canEdit = useMemo(() => {
    if (!sourceObservation) return true;
    return canEditObservation(sourceObservation, tifosiCle, {
      trustedMineList: routeState.trustedMineList,
      isAuthenticated: Boolean(googleId),
      profile: {
        pseudo: profilePseudo,
        email: profileEmail,
        prenom: profilePrenom,
        nom: profileNom,
      },
    });
  }, [
    sourceObservation,
    tifosiCle,
    googleId,
    routeState.trustedMineList,
    profilePseudo,
    profileEmail,
    profilePrenom,
    profileNom,
  ]);

  const isReadOnly = isEditMode && !canEdit;
  const loadProfile = useProfileStore((s) => s.loadProfile);
  const clubs = useClubStore((s) => s.clubs);
  const fetchClubs = useClubStore((s) => s.fetchClubs);
  const [matchFromApi, setMatchFromApi] = useState<Match | null>(null);
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const [privateBlocked, setPrivateBlocked] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const observationBlockMessage = useMemo(() => {
    if (isEditMode) return null;
    if (!matchFromApi) return 'Chargement des informations du match…';
    return getMatchObservationBlockMessage(matchFromApi, new Date(nowTick));
  }, [isEditMode, matchFromApi, nowTick]);

  const matchIdLookup = routeState.matchId || sourceObservation?.matchId || '';

  /** Ouverture via lien partagé (?id=…&match=…). */
  useEffect(() => {
    if (sourceObservation) return;
    const obsId = searchParams.get('id')?.trim();
    if (!obsId || shareHydratedRef.current === obsId) return;

    let cancelled = false;
    void (async () => {
      const matchParam = searchParams.get('match')?.trim();
      let found: Observation | undefined;

      if (matchParam) {
        const res = await apiService.getObservationsByMatch(matchParam);
        found = res.data?.find((o) => observationMatchesShareId(o, obsId));
      }
      if (!found) {
        const res = await apiService.getObservations();
        found = res.data?.find((o) => observationMatchesShareId(o, obsId));
      }
      if (cancelled || !found) return;

      shareHydratedRef.current = obsId;
      if (!isObservationVisibleToViewer(found, tifosiCle)) {
        setPrivateBlocked(true);
        return;
      }

      const mid = matchParam || found.matchId?.trim() || '';
      let matchState: Partial<ObservationRouteState> = {
        matchId: mid || found.matchId,
        homeTeam: found.homeTeam,
        awayTeam: found.awayTeam,
        homeLogo: found.home_logo,
        awayLogo: found.away_logo,
        matchCompetition: found.matchCompetition,
        matchPoule: found.matchPoule,
      };

      if (mid) {
        const mRes = await apiService.getMatchById(mid);
        const m = mRes?.data;
        if (m && !cancelled) {
          matchState = {
            ...matchState,
            matchId: m.cle || m.idmatch_football || mid,
            matchRouteId: m.idmatch_football || m.cle || mid,
            homeTeam: m.homeTeam,
            awayTeam: m.awayTeam,
            homeLogo: m.home_logo,
            awayLogo: m.away_logo,
            matchCompetition: m.competition?.trim() || matchState.matchCompetition,
            matchPoule: m.poule?.trim() || matchState.matchPoule,
            matchLatitude: m.latitude,
            matchLongitude: m.longitude,
          };
        }
      }

      navigate(
        { pathname: '/observation/detailed', search: location.search },
        {
          replace: true,
          state: {
            mode: 'edit',
            observation: found,
            ...matchState,
          } satisfies ObservationRouteState,
        },
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [sourceObservation, searchParams, navigate, location.search, tifosiCle]);

  useEffect(() => {
    if (!sourceObservation?.isPrivate) {
      setPrivateBlocked(false);
      return;
    }
    setPrivateBlocked(!isObservationVisibleToViewer(sourceObservation, tifosiCle));
  }, [sourceObservation, tifosiCle]);

  useEffect(() => {
    if (!matchIdLookup) {
      setMatchFromApi(null);
      return;
    }
    let cancelled = false;
    void apiService.getMatchById(String(matchIdLookup)).then((res) => {
      if (!cancelled && res?.data) setMatchFromApi(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [matchIdLookup]);

  /** Clé match GoPaaS pour `match_football` (événement d’usage). */
  const matchFootballForUsageEvent = useMemo(() => {
    const fromApi =
      (matchFromApi?.cle && String(matchFromApi.cle).trim()) ||
      (matchFromApi?.idmatch_football && String(matchFromApi.idmatch_football).trim()) ||
      '';
    if (fromApi) return fromApi;
    const fromRoute = String(routeState.matchId || '').trim();
    if (fromRoute) return fromRoute;
    return String(sourceObservation?.matchId || '').trim();
  }, [matchFromApi, routeState.matchId, sourceObservation?.matchId]);

  /** Ouverture d’une fiche existante uniquement (pas création : `observation_created` à l’envoi). Pas sur retour historique (`POP`). */
  useEffect(() => {
    if (navigationType === 'POP') return;
    if (!sourceObservation) return;
    if (!tifosiCle || !matchFootballForUsageEvent) return;
    trackUsageEvent(
      {
        type: 'observation_opened',
        tifosi: tifosiCle,
        match_football: matchFootballForUsageEvent,
      },
      { navigationType, dedupeContext: matchFootballForUsageEvent },
    );
  }, [tifosiCle, matchFootballForUsageEvent, navigationType, sourceObservation]);

  const homeTeamName =
    routeState.homeTeam?.trim() ||
    matchFromApi?.homeTeam?.trim() ||
    sourceObservation?.homeTeam?.trim() ||
    'Club 1';
  const awayTeamName =
    routeState.awayTeam?.trim() ||
    matchFromApi?.awayTeam?.trim() ||
    sourceObservation?.awayTeam?.trim() ||
    'Club 2';
  const homeLogoUri =
    routeState.homeLogo || matchFromApi?.home_logo || sourceObservation?.home_logo;
  const awayLogoUri =
    routeState.awayLogo || matchFromApi?.away_logo || sourceObservation?.away_logo;

  const homeClubFromStore = useMemo(
    () => findClubByName(clubs, homeTeamName),
    [clubs, homeTeamName],
  );
  const awayClubFromStore = useMemo(
    () => findClubByName(clubs, awayTeamName),
    [clubs, awayTeamName],
  );

  const teamSideContext = useMemo((): TeamSideContext => {
    return {
      homeClubCle: routeState.homeClubCle ?? homeClubFromStore?.cle,
      awayClubCle: routeState.awayClubCle ?? awayClubFromStore?.cle,
      homeClubId: routeState.homeClubId ?? homeClubFromStore?.idclub,
      awayClubId: routeState.awayClubId ?? awayClubFromStore?.idclub,
      homeTeamName,
      awayTeamName,
    };
  }, [
    routeState.homeClubCle,
    routeState.awayClubCle,
    routeState.homeClubId,
    routeState.awayClubId,
    homeClubFromStore,
    awayClubFromStore,
    homeTeamName,
    awayTeamName,
  ]);

  const matchCompetitionDisplay = useMemo(() => {
    return (
      routeState.matchCompetition?.trim() ||
      matchFromApi?.competition?.trim() ||
      sourceObservation?.matchCompetition?.trim() ||
      ''
    );
  }, [routeState.matchCompetition, matchFromApi?.competition, sourceObservation?.matchCompetition]);

  const matchPouleDisplay = useMemo(() => {
    return (
      routeState.matchPoule?.trim() ||
      matchFromApi?.poule?.trim() ||
      sourceObservation?.matchPoule?.trim() ||
      ''
    );
  }, [routeState.matchPoule, matchFromApi?.poule, sourceObservation?.matchPoule]);

  const matchCompetitionPouleLine = useMemo(() => {
    return [matchCompetitionDisplay, matchPouleDisplay].filter(Boolean).join(' ').trim();
  }, [matchCompetitionDisplay, matchPouleDisplay]);

  const teamSectionTitle = useMemo(() => {
    return matchCompetitionPouleLine ? `ÉQUIPES ${matchCompetitionPouleLine}` : 'ÉQUIPE';
  }, [matchCompetitionPouleLine]);

  const matchDetailPathId = useMemo(() => {
    return (
      routeState.matchRouteId?.trim() ||
      routeState.matchId?.trim() ||
      matchFromApi?.idmatch_football?.trim() ||
      matchFromApi?.cle?.trim() ||
      sourceObservation?.matchId?.trim() ||
      ''
    );
  }, [
    routeState.matchRouteId,
    routeState.matchId,
    matchFromApi?.idmatch_football,
    matchFromApi?.cle,
    sourceObservation?.matchId,
  ]);

  const ficheMetaLine = useMemo(() => {
    if (!sourceObservation) return null;
    const me = (tifosiCle || '').trim();
    const owner = (sourceObservation.tifosi || '').trim();
    const viewerName =
      [profilePrenom?.trim(), profileNom?.trim()].filter(Boolean).join(' ').trim() ||
      profilePseudo?.trim() ||
      '';
    const fallbackAuthor = me && owner && me === owner && viewerName ? viewerName : '';
    return formatFicheObservationMeta(sourceObservation, fallbackAuthor || undefined);
  }, [sourceObservation, tifosiCle, profilePrenom, profileNom, profilePseudo]);

  const matchLat = Number(routeState.matchLatitude ?? matchFromApi?.latitude ?? '');
  const matchLng = Number(routeState.matchLongitude ?? matchFromApi?.longitude ?? '');
  const canVerifyGeo = Number.isFinite(matchLat) && Number.isFinite(matchLng);
  const [form, setForm] = useState<ObservationFormState>(defaultFormState);
  const [expertMode, setExpertMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [geoState, setGeoState] = useState<GeoVerificationState>('not_requested');
  const [geoDistanceM, setGeoDistanceM] = useState<number | null>(null);
  const [geoAccuracyM, setGeoAccuracyM] = useState<number | null>(null);
  const [geoInfoText, setGeoInfoText] = useState<string>('');
  const [geoVerifiedAt, setGeoVerifiedAt] = useState<string | null>(null);
  const hasAutoVerifiedRef = useRef(false);
  const playerSectionRef = useRef<HTMLDivElement | null>(null);
  const teamSectionRef = useRef<HTMLDivElement | null>(null);
  const ratingsSectionRef = useRef<HTMLDivElement | null>(null);
  const commentsSectionRef = useRef<HTMLDivElement | null>(null);
  const commentsTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const commentDictationRef = useRef<SpeechRecognition | null>(null);
  const dictationCommentsSnapshotRef = useRef('');
  const [commentDictationActive, setCommentDictationActive] = useState(false);
  const [commentTemplatesSheetVisible, setCommentTemplatesSheetVisible] = useState(false);
  const [commentTemplatesAll, setCommentTemplatesAll] = useState<CommentTemplate[]>([]);
  const [advancedCommentTemplates, setAdvancedCommentTemplates] = useState(loadAdvancedCommentTemplatesPreference);
  const [dictationError, setDictationError] = useState<string | null>(null);

  const commentDictationSupported = useMemo(() => getSpeechRecognitionConstructor() !== null, []);

  const completionRate = useMemo(() => {
    const steps = [
      form.nom.trim().length > 0,
      form.prenom.trim().length > 0,
      form.numero.trim().length > 0,
      form.poste.trim().length > 0,
      form.noteGlobale > 0,
    ];
    const completed = steps.filter(Boolean).length;
    return Math.round((completed / steps.length) * 100);
  }, [form]);
  const contentQuality = useMemo(() => getContentQuality(form), [form]);

  const commentTemplatesForPoste = useMemo(
    () => filterCommentTemplatesForPoste(commentTemplatesAll, form.poste, advancedCommentTemplates),
    [commentTemplatesAll, form.poste, advancedCommentTemplates],
  );

  const handleAdvancedCommentTemplatesChange = (enabled: boolean) => {
    setAdvancedCommentTemplates(enabled);
    saveAdvancedCommentTemplatesPreference(enabled);
  };

  useEffect(() => {
    let cancelled = false;
    void apiService.getCommentTemplates().then((result) => {
      if (!cancelled) setCommentTemplatesAll(result.data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!commentDictationActive) return;
    const el = commentsTextareaRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [form.comments, commentDictationActive]);

  const updateField = <K extends keyof ObservationFormState>(key: K, value: ObservationFormState[K]) => {
    if (isReadOnly) return;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const applyGlobalStars = (stars: number) => {
    if (isReadOnly) return;
    const noteGlobale = clamp(stars, 0, MAX_STARS);
    const criterion = starsToCriterion(noteGlobale);
    setForm((prev) => ({
      ...prev,
      noteGlobale,
      technique: criterion,
      physique: criterion,
      mental: criterion,
      tactique: criterion,
      vitesse: criterion,
    }));
  };

  const applyCriterion = (
    key: 'technique' | 'physique' | 'mental' | 'tactique' | 'vitesse',
    value: number,
  ) => {
    if (isReadOnly) return;
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      next.noteGlobale = criteriaToStars(
        next.technique,
        next.physique,
        next.mental,
        next.tactique,
        next.vitesse,
      );
      return next;
    });
  };

  const handleNumeroChange = (value: string) => {
    if (isReadOnly) return;
    const sanitized = value.replace(/[^\d]/g, '').slice(0, 2);
    const inferredPoste = inferPosteFromNumero(sanitized);
    setForm((prev) => ({
      ...prev,
      numero: sanitized,
      poste: inferredPoste ?? prev.poste,
    }));
  };

  const jumpTo = (section: 'player' | 'team' | 'ratings' | 'comments') => {
    const map = {
      player: playerSectionRef,
      team: teamSectionRef,
      ratings: ratingsSectionRef,
      comments: commentsSectionRef,
    };
    map[section].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const appendCommentTemplates = (templates: string[]) => {
    if (isReadOnly || templates.length === 0) return;
    const bullets = templates.map((t) => `- ${t}`);
    const next = form.comments.trim() ? `${form.comments.trim()}\n${bullets.join('\n')}` : bullets.join('\n');
    updateField('comments', next);
  };

  useEffect(() => {
    return () => {
      const r = commentDictationRef.current;
      if (r) {
        try {
          r.abort();
        } catch {
          /* ignore */
        }
        commentDictationRef.current = null;
      }
    };
  }, []);

  const stopCommentDictation = () => {
    const r = commentDictationRef.current;
    if (!r) {
      setCommentDictationActive(false);
      return;
    }
    try {
      r.stop();
    } catch {
      try {
        r.abort();
      } catch {
        /* ignore */
      }
    }
  };

  const toggleCommentDictation = () => {
    if (isReadOnly || !commentDictationSupported) return;
    if (commentDictationActive) {
      stopCommentDictation();
      return;
    }
    const Ctor = getSpeechRecognitionConstructor();
    if (!Ctor) {
      setDictationError('Dictée vocale non disponible sur ce navigateur.');
      return;
    }
    setDictationError(null);
    try {
      commentDictationRef.current?.abort();
    } catch {
      /* ignore */
    }
    dictationCommentsSnapshotRef.current = form.comments;
    const recognition = new Ctor();
    recognition.lang = 'fr-FR';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let sessionLive = '';
      for (let i = 0; i < event.results.length; i += 1) {
        sessionLive += event.results[i][0].transcript;
      }
      const snap = dictationCommentsSnapshotRef.current;
      const liveTrimmed = sessionLive.trimEnd();
      const combined =
        snap.trim() === '' ? liveTrimmed : liveTrimmed ? `${snap.trimEnd()}\n${liveTrimmed}` : snap;
      setForm((prev) => ({ ...prev, comments: combined }));
    };
    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error === 'aborted') return;
      if (event.error === 'no-speech') return;
      if (event.error === 'not-allowed') {
        setDictationError('Accès au microphone refusé.');
      } else {
        setDictationError('Dictée interrompue.');
      }
      setCommentDictationActive(false);
      commentDictationRef.current = null;
    };
    recognition.onend = () => {
      setCommentDictationActive(false);
      commentDictationRef.current = null;
    };
    commentDictationRef.current = recognition;
    try {
      recognition.start();
      setCommentDictationActive(true);
    } catch {
      setDictationError('Impossible de démarrer la dictée.');
      commentDictationRef.current = null;
      setCommentDictationActive(false);
    }
  };

  const verifyProximity = async () => {
    if (!canVerifyGeo) {
      setGeoState('not_available');
      setGeoInfoText('Position du lieu du match indisponible.');
      return;
    }
    if (!navigator.geolocation) {
      setGeoState('not_available');
      setGeoInfoText('La géolocalisation n’est pas disponible sur cet appareil.');
      return;
    }

    setGeoState('pending');
    setGeoInfoText('Vérification de position en cours...');

    const result = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });
    }).catch((error: GeolocationPositionError) => error);

    if ('code' in result) {
      setGeoState('uncertain');
      setGeoInfoText('Vérification impossible (permission refusée ou signal GPS indisponible).');
      return;
    }

    const distance = haversineDistanceMeters(matchLat, matchLng, result.coords.latitude, result.coords.longitude);
    const accuracy = Math.max(0, result.coords.accuracy || 0);
    const dynamicThreshold = Math.max(GEO_DISTANCE_THRESHOLD_M, accuracy * 2);
    const isNear = distance <= dynamicThreshold;

    setGeoDistanceM(distance);
    setGeoAccuracyM(accuracy);
    setGeoState(isNear ? 'verified_nearby' : 'verified_far');
    setGeoVerifiedAt(new Date().toISOString());
    setGeoInfoText(
      isNear
        ? `Position cohérente avec le lieu du match (seuil ${GEO_DISTANCE_THRESHOLD_M} m).`
        : `Position hors zone du lieu du match (seuil ${GEO_DISTANCE_THRESHOLD_M} m).`
    );
  };

  useEffect(() => {
    if (!canVerifyGeo) return;
    if (hasAutoVerifiedRef.current) return;
    hasAutoVerifiedRef.current = true;
    void verifyProximity();
  }, [canVerifyGeo]);

  useEffect(() => { void loadProfile(); }, [loadProfile]);
  useEffect(() => {
    if (!sourceObservation) return;
    void fetchClubs();
  }, [sourceObservation, fetchClubs]);
  useEffect(() => {
    if (!sourceObservation) return;
    const playerNumber = (sourceObservation.playerNumber || '').replace(/[^\d]/g, '');
    const selectedTeam = resolveObservationTeamSide(sourceObservation, teamSideContext);
    const technique = Number.isFinite(sourceObservation.technique) ? Number(sourceObservation.technique) : 6;
    const physique = Number.isFinite(sourceObservation.physique) ? Number(sourceObservation.physique) : 6;
    const mental = Number.isFinite(sourceObservation.mental) ? Number(sourceObservation.mental) : 6;
    const tactique = Number.isFinite(sourceObservation.tactique) ? Number(sourceObservation.tactique) : 6;
    const vitesse = Number.isFinite(sourceObservation.vitesse) ? Number(sourceObservation.vitesse) : 6;
    setForm((prev) => ({
      ...prev,
      nom: sourceObservation.nom || '',
      prenom: sourceObservation.prenom || '',
      poste: sourceObservation.poste || '',
      numero: playerNumber,
      selectedTeam,
      technique,
      physique,
      mental,
      tactique,
      vitesse,
      noteGlobale: criteriaToStars(technique, physique, mental, tactique, vitesse),
      nbButs: Number.isFinite(sourceObservation.nbButs) ? Number(sourceObservation.nbButs) : prev.nbButs,
      nbPassesDecisives: Number.isFinite(sourceObservation.nbPassesDecisives)
        ? Number(sourceObservation.nbPassesDecisives)
        : prev.nbPassesDecisives,
      isPrivate: resolveObservationPrivate(
        sourceObservation.isPrivate,
        sourceObservation.cle || sourceObservation.id,
      ),
      comments: sourceObservation.comments || '',
    }));
    if (
      criteriaSpread(technique, physique, mental, tactique, vitesse) >= 2 ||
      Boolean(sourceObservation.comments?.trim())
    ) {
      setExpertMode(true);
    }
  }, [sourceObservation, teamSideContext]);

  const finalizeObservation = async () => {
    if (isSubmitting) return;
    if (isReadOnly) return;
    if (!isEditMode) {
      if (!matchFromApi) {
        setSubmitError('Chargement des informations du match… Réessaie dans un instant.');
        return;
      }
      const blockMessage = getMatchObservationBlockMessage(matchFromApi);
      if (blockMessage) {
        setSubmitError(blockMessage);
        return;
      }
    }
    if (!form.numero.trim()) {
      setSubmitError('Le numéro est obligatoire.');
      return;
    }
    const targetMatchId = routeState.matchId || sourceObservation?.matchId;
    if (!targetMatchId) {
      setSubmitError("Identifiant du match manquant. Reviens à la fiche match puis réessaie.");
      return;
    }
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const cle = sourceObservation?.cle || (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
      const baseObservation = form.comments.trim();
      const payload = {
        cle,
        nom: form.nom.trim(),
        prenom: form.prenom.trim(),
        poste: form.poste.trim().toUpperCase(),
        numero: form.numero.trim(),
        technique: String(form.technique),
        tactique: String(form.tactique),
        mental: String(form.mental),
        vitesse: String(form.vitesse),
        physique: String(form.physique),
        but: String(form.nbButs),
        passe_decisive: String(form.nbPassesDecisives),
        observations_generales: baseObservation || null,
        match_football: String(targetMatchId),
        equipe: form.selectedTeam,
        club:
          form.selectedTeam === 'home'
            ? teamSideContext.homeClubCle
            : teamSideContext.awayClubCle,
        tifosi: tifosiCle || undefined,
        home_team: routeState.homeTeam ?? matchFromApi?.homeTeam ?? sourceObservation?.homeTeam ?? undefined,
        away_team: routeState.awayTeam ?? matchFromApi?.awayTeam ?? sourceObservation?.awayTeam ?? undefined,
        home_club_id: teamSideContext.homeClubId ?? undefined,
        away_club_id: teamSideContext.awayClubId ?? undefined,
        prive: form.isPrivate ? ('1' as const) : ('0' as const),
        qualite_contenu_score: contentQuality.score,
        qualite_contenu_niveau: mapQualityLevel(contentQuality.level),
        qualite_commentaire_mots: contentQuality.wordCount,
        qualite_commentaire_concret: boolToFlag(contentQuality.hasConcreteElements),
        qualite_notes_coherence: boolToFlag(contentQuality.hasStructuredNotes),
        geo_verification_statut: mapGeoStatus(geoState),
        geo_distance_m: geoDistanceM != null ? Math.round(geoDistanceM) : null,
        geo_accuracy_m: geoAccuracyM != null ? Math.round(geoAccuracyM) : null,
        geo_seuil_m: GEO_DISTANCE_THRESHOLD_M,
        geo_verifie_at: geoVerifiedAt,
      };
      const ratingValues = [form.technique, form.tactique, form.mental, form.vitesse, form.physique]
        .filter((v) => Number.isFinite(v));
      const averageRating = ratingValues.length
        ? ratingValues.reduce((acc, value) => acc + value, 0) / ratingValues.length
        : 0;
      const trustInput: Observation = {
        id: `draft-${cle}`,
        cle,
        tifosi: tifosiCle || undefined,
        nom: form.nom.trim(),
        prenom: form.prenom.trim(),
        playerName: `${form.prenom.trim()} ${form.nom.trim()}`.trim() || 'Joueur',
        playerNumber: form.numero.trim() ? `#${form.numero.trim()}` : undefined,
        poste: form.poste.trim().toUpperCase() || undefined,
        rating: averageRating,
        date: new Date().toISOString(),
        qualityScore: contentQuality.score,
        qualityWordCount: contentQuality.wordCount,
        qualityCommentConcrete: boolToFlag(contentQuality.hasConcreteElements),
        qualityNotesCoherent: boolToFlag(contentQuality.hasStructuredNotes),
        geoStatus: mapGeoStatus(geoState),
        geoDistanceM: geoDistanceM != null ? Math.round(geoDistanceM) : null,
        geoAccuracyM: geoAccuracyM != null ? Math.round(geoAccuracyM) : null,
        geoThresholdM: GEO_DISTANCE_THRESHOLD_M,
        geoVerifiedAt,
      };
      const trustScore = computeObservationTrust(trustInput);
      const aggregate = aggregateObservationScores([{ ...trustInput, trustScore }]);
      Object.assign(payload, {
        score_confiance: Math.round(aggregate.confidenceScore),
        niveau_confiance: aggregate.confidenceLevel,
        note_robuste: Number(aggregate.robustRating.toFixed(2)),
        observation_provisoire: aggregate.isProvisional ? 1 : 0,
      });
      await apiService.createObservation(payload);
      setLocalObservationPrivate(cle, form.isPrivate, tifosiCle);
      if (!isEditMode && tifosiCle) {
        const matchKey = String(targetMatchId).trim();
        if (matchKey) {
          trackUsageEvent(
            { type: 'observation_created', tifosi: tifosiCle, match_football: matchKey },
            { skipOnPop: false, dedupeMs: 0, dedupeContext: payload.cle || matchKey },
          );
        }
      }
      const returnId = routeState.matchRouteId || routeState.matchId;
      if (tifosiCle) {
        const ids = new Set<string>();
        const add = (v: string | undefined) => {
          const s = String(v ?? '').trim();
          if (s) ids.add(s);
        };
        add(returnId);
        add(routeState.matchRouteId);
        add(routeState.matchId);
        add(String(targetMatchId));
        add(matchFromApi?.cle);
        add(matchFromApi?.idmatch_football);
        if (ids.size > 0) {
          try {
            const exp = Date.now() + 8000;
            sessionStorage.setItem(
              `gopaas_skip_match_opened:${tifosiCle}`,
              JSON.stringify({ ids: [...ids], exp }),
            );
          } catch {
            /* ignore */
          }
        }
      }
      if (returnId) {
        navigate(`/match/${returnId}`, { replace: true });
      } else {
        navigate(-1);
      }
    } catch {
      setSubmitError("Échec d'envoi vers GoPaaS. Vérifie la connexion puis réessaie.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelObservation = () => {
    navigate(-1);
  };

  const openMatchDetail = () => {
    if (!matchDetailPathId) return;
    navigate(`/match/${matchDetailPathId}`);
  };

  const handleShare = async () => {
    const obsId = (sourceObservation?.cle || sourceObservation?.id || '').trim();
    const url = obsId
      ? buildObservationShareUrl(obsId, matchDetailPathId || undefined)
      : matchDetailPathId
        ? buildMatchShareUrl(matchDetailPathId)
        : buildAppUrl('observation/detailed');

    const playerLabel = sourceObservation
      ? formatObservationPlayerLabel(sourceObservation)
      : [form.prenom, form.nom].filter(Boolean).join(' ').trim() ||
        (form.numero.trim() ? `N°${form.numero.replace(/[^\d]/g, '')}` : 'Joueur');

    const title = `Observation — ${playerLabel}`;
    const matchLine = `${homeTeamName} vs ${awayTeamName}`;
    const compSuffix = matchCompetitionPouleLine ? ` — ${matchCompetitionPouleLine}` : '';
    const text = `${matchLine}${compSuffix}`;

    const result = await shareContent({ title, text, url });
    const feedback = shareResultToFeedback(result);
    if (feedback) setShareFeedback(feedback);
    window.setTimeout(() => setShareFeedback(null), 2200);
  };

  if (privateBlocked) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, backgroundColor: colors.background }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottom: `1px solid ${Colors.primary}15` }}>
          <button onClick={() => navigate(-1)} style={{ width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `${Colors.primary}15`, cursor: 'pointer' }}>
            <Icon name="chevron-back" size={24} color={Colors.primary} />
          </button>
          <span style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>Fiche observation</span>
          <div style={{ width: 40 }} />
        </div>
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Icon name="lock-closed-outline" size={36} color={colors.textSecondary} />
          <div style={{ marginTop: 12, fontSize: 16, fontWeight: 700, color: colors.text }}>Observation privée</div>
          <div style={{ marginTop: 8, fontSize: 13, color: colors.textSecondary, lineHeight: 1.45 }}>
            Cette observation n’est visible que par son auteur.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, backgroundColor: colors.background }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottom: `1px solid ${Colors.primary}15` }}>
        <button onClick={() => navigate(-1)} style={{ width: 40, height: 40, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `${Colors.primary}15`, cursor: 'pointer' }}>
          <Icon name="chevron-back" size={24} color={Colors.primary} />
        </button>
        <span style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>
          {isEditMode ? 'Modifier observation' : 'Fiche observation'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            type="button"
            onClick={() => void handleShare()}
            aria-label="Partager"
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              background: 'transparent',
              border: 'none',
            }}
          >
            <Icon name="share-outline" size={24} color={colors.text} />
          </button>
          <button
            type="button"
            onClick={openMatchDetail}
            disabled={!matchDetailPathId}
            aria-label="Ouvrir la fiche match"
            title="Fiche match"
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${Colors.primary}15`,
              cursor: matchDetailPathId ? 'pointer' : 'not-allowed',
              border: 'none',
              opacity: matchDetailPathId ? 1 : 0.45,
            }}
          >
            <Icon name="football-outline" size={22} color={Colors.primary} />
          </button>
        </div>
      </div>

      {shareFeedback && (
        <div style={{ padding: '6px 16px', backgroundColor: `${Colors.primary}15`, color: Colors.primary, fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
          {shareFeedback}
        </div>
      )}

      {!isEditMode && observationBlockMessage && observationBlockMessage !== 'Chargement des informations du match…' ? (
        <div
          style={{
            margin: '0 16px 8px',
            padding: '10px 12px',
            borderRadius: 12,
            fontSize: 12,
            lineHeight: 1.45,
            color: colors.textSecondary,
            backgroundColor: `${Colors.primary}12`,
            border: `1px solid ${Colors.primary}25`,
          }}
        >
          {observationBlockMessage}
        </div>
      ) : null}

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: 16, paddingBottom: 140 }}>
        {isReadOnly && (
          <div style={{ marginBottom: 12, borderRadius: 12, padding: 10, border: `1px solid ${colors.border}`, backgroundColor: colors.surface, color: colors.textSecondary, fontSize: 12 }}>
            Cette fiche est en lecture seule. Seul le createur peut la modifier.
          </div>
        )}
        {ficheMetaLine ? (
          <div
            style={{
              marginBottom: 12,
              fontSize: 13,
              fontWeight: 600,
              color: colors.textSecondary,
              textAlign: 'center',
            }}
          >
            {ficheMetaLine}
          </div>
        ) : null}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, color: colors.textSecondary }}>Progression</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: colors.textSecondary }}>{completionRate}%</span>
          </div>
          <div style={{ height: 4, borderRadius: 999, overflow: 'hidden', backgroundColor: colors.border }}>
            <div style={{ width: `${completionRate}%`, height: '100%', backgroundColor: Colors.primary, transition: 'width 0.2s ease' }} />
          </div>
        </div>

        <div style={{ display: 'none', ...styles.geoCard, borderColor: colors.border, backgroundColor: colors.surface }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: colors.text }}>Vérification de proximité (RGPD)</div>
              <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                Optionnel - aucune coordonnée brute n’est conservée.
              </div>
            </div>
            <button
              onClick={verifyProximity}
              disabled={geoState === 'pending'}
              style={{ ...styles.geoButton, borderColor: colors.border, color: colors.textSecondary, opacity: geoState === 'pending' ? 0.7 : 1 }}
            >
              {geoState === 'pending' ? 'Vérification...' : 'Vérifier'}
            </button>
          </div>
          {geoInfoText ? (
            <div style={{ marginTop: 8, fontSize: 12, color: geoState === 'verified_far' ? '#ff9f43' : colors.textSecondary }}>
              {geoInfoText}
            </div>
          ) : null}
        </div>

        <div ref={playerSectionRef} style={{ marginBottom: 24 }}>
          <div style={styles.sectionLabel}>JOUEUR</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <input className="tifosi-field" style={fieldStyle} value={form.nom} onChange={(e) => updateField('nom', e.target.value)} placeholder="Nom" disabled={isReadOnly} />
            <input className="tifosi-field" style={fieldStyle} value={form.prenom} onChange={(e) => updateField('prenom', e.target.value)} placeholder="Prénom" disabled={isReadOnly} />
            <input className="tifosi-field" style={fieldStyle} value={form.numero} onChange={(e) => handleNumeroChange(e.target.value)} placeholder="Numéro *" required disabled={isReadOnly} />
            <select
              className="tifosi-field"
              style={fieldStyle}
              value={form.poste}
              onChange={(e) => updateField('poste', e.target.value)}
              disabled={isReadOnly}
            >
              <option value="" style={selectOptionStyle}>
                Choisir un poste
              </option>
              {posteOptions.map((option) => (
                <option key={option} value={option} style={selectOptionStyle}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div ref={teamSectionRef} style={{ marginBottom: 24 }}>
          <div
            style={{
              ...styles.sectionLabel,
              textAlign: matchCompetitionPouleLine ? 'left' : undefined,
            }}
          >
            {teamSectionTitle}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            {(['home', 'away'] as const).map((t) => (
              (() => {
                const isHome = t === 'home';
                const teamName = isHome ? homeTeamName : awayTeamName;
                const teamLogo = isHome ? homeLogoUri : awayLogoUri;
                return (
              <button key={t} onClick={() => updateField('selectedTeam', t)} disabled={isReadOnly} style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 16, paddingBottom: 16, borderRadius: 16,
                border: form.selectedTeam === t ? `2px solid ${Colors.primary}` : '2px solid transparent', gap: 4, cursor: 'pointer',
                backgroundColor: form.selectedTeam === t ? `${Colors.primary}08` : colors.surface,
                opacity: isReadOnly ? 0.7 : 1,
              }}>
                {teamLogo ? (
                  <div style={{ width: 34, height: 34, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `${colors.background}90` }}>
                    <LogoImage uri={teamLogo} size={28} />
                  </div>
                ) : (
                  <Icon name="football" size={28} color={form.selectedTeam === t ? Colors.primary : colors.textSecondary} />
                )}
                <span style={{ fontSize: 14, fontWeight: 700, color: form.selectedTeam === t ? Colors.primary : colors.textSecondary }}>
                  {teamName}
                </span>
              </button>
                );
              })()
            ))}
          </div>
        </div>

        <div ref={ratingsSectionRef} style={{ marginBottom: 24 }}>
          <div style={styles.sectionLabel}>NOTES</div>
          <div style={{ borderRadius: 16, padding: 16, marginBottom: 12, backgroundColor: colors.surface }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>Note globale</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: RATING_NOTE_COLOR }}>
                {form.noteGlobale}/{MAX_STARS}
              </span>
            </div>
            <StarRating
              rating={form.noteGlobale}
              maxRating={MAX_STARS}
              size={26}
              readonly={isReadOnly}
              onRatingChange={applyGlobalStars}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <CriteriaSlider
              label="Nb buts"
              value={form.nbButs}
              onChange={(v) => updateField('nbButs', v)}
              min={0}
              showMax={false}
              colors={colors}
              disabled={isReadOnly}
            />
            <CriteriaSlider
              label="Nb passe D"
              value={form.nbPassesDecisives}
              onChange={(v) => updateField('nbPassesDecisives', v)}
              min={0}
              showMax={false}
              colors={colors}
              disabled={isReadOnly}
            />
          </div>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 12,
              padding: '12px 14px',
              borderRadius: 16,
              backgroundColor: colors.surface,
              cursor: isReadOnly ? 'default' : 'pointer',
              opacity: isReadOnly ? 0.75 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={form.isPrivate}
              onChange={(e) => updateField('isPrivate', e.target.checked)}
              disabled={isReadOnly}
              style={{ width: 18, height: 18, accentColor: Colors.primary, cursor: isReadOnly ? 'default' : 'pointer' }}
            />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: colors.text }}>Mode privé</span>
              <span style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>
                {form.isPrivate
                  ? 'Visible uniquement par vous'
                  : 'Observation publique — visible par les autres tifosi'}
              </span>
            </span>
            <Icon name="lock-closed-outline" size={18} color={form.isPrivate ? Colors.primary : colors.textSecondary} />
          </label>

          <button
            type="button"
            onClick={() => {
              setExpertMode((v) => {
                if (v) setCommentTemplatesSheetVisible(false);
                return !v;
              });
            }}
            aria-expanded={expertMode}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              padding: '12px 14px',
              borderRadius: 16,
              border: `1px solid ${expertMode ? `${Colors.primary}55` : colors.border}`,
              backgroundColor: expertMode ? `${Colors.primary}10` : colors.surface,
              cursor: 'pointer',
              marginBottom: expertMode ? 12 : 0,
            }}
          >
            <span style={{ textAlign: 'left' }}>
              <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: colors.text }}>Mode expert</span>
              <span style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>
                Critères détaillés et commentaires
              </span>
            </span>
            <Icon
              name="chevron-down"
              size={20}
              color={colors.textSecondary}
              style={{ transform: expertMode ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}
            />
          </button>

          {expertMode ? (
            <div>
              <CriteriaSlider label="Technique" value={form.technique} onChange={(v) => applyCriterion('technique', v)} colors={colors} disabled={isReadOnly} />
              <CriteriaSlider label="Physique" value={form.physique} onChange={(v) => applyCriterion('physique', v)} colors={colors} disabled={isReadOnly} />
              <CriteriaSlider label="Vitesse" value={form.vitesse} onChange={(v) => applyCriterion('vitesse', v)} colors={colors} disabled={isReadOnly} />
              <CriteriaSlider label="Mental" value={form.mental} onChange={(v) => applyCriterion('mental', v)} colors={colors} disabled={isReadOnly} />
              <CriteriaSlider label="Tactique" value={form.tactique} onChange={(v) => applyCriterion('tactique', v)} colors={colors} disabled={isReadOnly} />

              <div ref={commentsSectionRef} style={{ marginTop: 12 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                    paddingLeft: 4,
                    paddingRight: 4,
                  }}
                >
                  <div style={{ ...styles.sectionLabel, marginBottom: 0 }}>COMMENTAIRES</div>
                  {!isReadOnly ? (
                    <button
                      type="button"
                      onClick={() => setCommentTemplatesSheetVisible(true)}
                      disabled={commentDictationActive}
                      aria-label="Phrases types"
                      title="Plus"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 999,
                        border: `1px solid ${colors.border}`,
                        backgroundColor: colors.surface,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        opacity: commentDictationActive ? 0.5 : 1,
                        cursor: commentDictationActive ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <Icon name="ellipsis-vertical" size={20} color={colors.textSecondary} />
                    </button>
                  ) : null}
                </div>
                {!form.poste.trim() ? (
                  <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 10 }}>
                    Choisissez un poste pour des suggestions adaptées au profil.
                  </div>
                ) : null}
                <CommentTemplatesSheet
                  visible={commentTemplatesSheetVisible}
                  onClose={() => setCommentTemplatesSheetVisible(false)}
                  templates={commentTemplatesForPoste}
                  existingComments={form.comments}
                  onInsert={appendCommentTemplates}
                  disabled={isReadOnly || commentDictationActive}
                  advancedMode={advancedCommentTemplates}
                  onAdvancedModeChange={handleAdvancedCommentTemplatesChange}
                />
                <div style={{ borderRadius: 16, padding: 16, backgroundColor: colors.surface }}>
                  <div style={{ position: 'relative' }}>
                    <textarea
                      ref={commentsTextareaRef}
                      className="tifosi-field"
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        paddingRight: isReadOnly ? 0 : 52,
                        paddingBottom: isReadOnly ? 0 : 8,
                        fontSize: 16,
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        minHeight: 100,
                        resize: 'vertical',
                        color: colors.text,
                        fontFamily: 'inherit',
                      }}
                      value={form.comments}
                      onChange={(e) => {
                        updateField('comments', e.target.value);
                        if (dictationError) setDictationError(null);
                      }}
                      placeholder="Ajoutez des détails sur la performance, le positionnement et le potentiel du joueur..."
                      disabled={isReadOnly}
                      readOnly={commentDictationActive}
                    />
                    {!isReadOnly ? (
                      <button
                        type="button"
                        onClick={toggleCommentDictation}
                        disabled={!commentDictationSupported}
                        aria-pressed={commentDictationActive}
                        title={
                          !commentDictationSupported
                            ? 'Dictée vocale non disponible'
                            : commentDictationActive
                              ? 'Arrêter la dictée'
                              : 'Dictée vocale (français)'
                        }
                        style={{
                          position: 'absolute',
                          right: 0,
                          bottom: 4,
                          zIndex: 1,
                          width: 44,
                          height: 44,
                          borderRadius: 12,
                          border: `1px solid ${commentDictationActive ? Colors.primary : colors.border}`,
                          backgroundColor: commentDictationActive ? `${Colors.primary}22` : `${colors.surface}f2`,
                          backdropFilter: 'blur(6px)',
                          cursor: commentDictationSupported ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: commentDictationSupported ? 1 : 0.45,
                          boxShadow: `0 1px 4px ${colors.border}40`,
                        }}
                      >
                        <Icon
                          name={commentDictationActive ? 'mic' : 'mic-outline'}
                          size={22}
                          color={commentDictationActive ? Colors.primary : colors.textSecondary}
                        />
                      </button>
                    ) : null}
                  </div>
                  {dictationError ? (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#ff6b6b' }}>{dictationError}</div>
                  ) : commentDictationActive ? (
                    <div style={{ marginTop: 8, fontSize: 12, color: colors.textSecondary }}>
                      Écoute en cours - Arrêtez la dictée pour modifier le champ ou utiliser les modèles de commentaires.
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {!commentTemplatesSheetVisible ? (
        <div style={{ borderTop: `1px solid ${Colors.primary}15`, padding: 12, display: 'flex', gap: 10, backgroundColor: colors.background }}>
          <button
            onClick={cancelObservation}
            style={{ ...styles.footerButton, backgroundColor: colors.surface, color: colors.textSecondary, borderColor: colors.border }}
          >
            Annuler
          </button>
          <button
            onClick={finalizeObservation}
            disabled={isSubmitting || isReadOnly || Boolean(observationBlockMessage)}
            style={{
              ...styles.footerButton,
              backgroundColor: Colors.primary,
              color: '#ffffff',
              borderColor: Colors.primary,
              opacity: isSubmitting || isReadOnly || observationBlockMessage ? 0.7 : 1,
            }}
          >
            {isReadOnly ? 'Lecture seule' : isSubmitting ? 'Envoi...' : isEditMode ? 'Enregistrer' : 'Envoyer'}
          </button>
        </div>
      ) : null}
      {submitError && (
        <div style={{ padding: '0 12px 12px', fontSize: 12, color: '#ff6b6b' }}>{submitError}</div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  sectionLabel: { fontSize: 12, fontWeight: 600, color: `${Colors.primary}cc`, letterSpacing: 1, marginBottom: 12, paddingLeft: 4, paddingRight: 4 },
  geoCard: { border: '1px solid', borderRadius: 12, padding: 12, marginBottom: 16 },
  geoButton: { height: 34, border: '1px solid', borderRadius: 10, background: 'transparent', padding: '0 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  rangeInput: { flex: 1, cursor: 'pointer' },
  stepperButton: { width: 30, height: 30, borderRadius: 999, border: '1px solid', cursor: 'pointer', fontSize: 18, lineHeight: '18px', fontWeight: 700 },
  footerButton: { flex: 1, height: 46, borderRadius: 12, border: '1px solid', cursor: 'pointer', fontSize: 15, fontWeight: 700 },
};
